import axios from '@/axios';

/**
 * @typedef {{
 *     id:         number,
 *     username:   string,
 *     first_name: ?string,
 *     last_name:  ?string,
 *     email:      string,
 *     visa:       ?string,
 * }} ReviewCommentUser
 */

export default class ReviewComment
{
    // ------------------------------------------------------------ MEMBERS

    /** @type {number} */
    id = 0;

    /** @type {boolean} */
    private = false;

    /** @type {string} */
    content = '';

    /** @type {number} */
    review_request_id = 0;

    /** @type {number} */
    user_id = 0;

    /** @type {ReviewCommentUser} */
    user = null;

    /** @type {string} */
    created_at = '';

    /** @type {string} */
    updated_at = '';

    /** @type {string} */
    deleted_at = '';

    // ------------------------------------------------------------ CONSTRUCTOR

    /**
     * Construct a new ReviewComment object.
     *
     * @param {Object}            [fields]
     * @param {number}            fields.id
     * @param {boolean}           fields.private
     * @param {string}            fields.content
     * @param {number}            fields.review_request_id
     * @param {number}            fields.user_id
     * @param {ReviewCommentUser} fields.user
     * @param {string}            fields.created_at
     * @param {string}            fields.updated_at
     * @param {string}            fields.deleted_at
     */
    constructor(fields = {})
    {
        for(const key of Object.getOwnPropertyNames(this))
        {
            if(fields.hasOwnProperty(key))
            {
                this[key] = fields[key];
            }
        }
    }

    // ------------------------------------------------------------ STATIC

    /**
     * Fetch all comments for a review request.
     *
     * @param {number} requestId
     * @returns {ReviewComment[]}
     */
    static async fetchByRequest(requestId)
    {
        const url = `/api/review-request/${requestId}/comment`;
        const response = await axios.get(url);
        return response.data.map(c => new ReviewComment(c));
    }

    /**
     * Retrieve the comments linked to an order.
     *
     * @param {number} reference
     * @returns {ReviewComment[]}
     */
    static async fetchByOrder(reference)
    {
        const url = `/api/order/${reference}/review-request/comment`;
        const response = await axios.get(url);
        return response.data.map(c => new ReviewComment(c));
    }

    /**
     * Retrieve the timestamp of the last comment linked to an order.
     *
     * @param {number} ref The order's reference number.
     * @returns {number} A Unix timestamp in seconds.
     */
    static async fetchLastDateByOrder(ref)
    {
        if(typeof ref !== 'number')
        {
            throw new TypeError(`reference must be a number, got ${typeof ref}`);
        }

        const url = `/api/order/${ref}/review-request/comment/last/timestamp`;
        const response = await axios.get(url);
        return response.data;
    }

    /**
     * Retrieve the timestamps of the last comment linked to each order which is
     * currently waiting for review.
     *
     * @returns {Map<number, number>} A map where each key is an order reference
     *                                and each value is a Unix timestamp in
     *                                seconds.
     */
    static async fetchLastDateForActive()
    {
        const url = `/api/review-request/active/comment/last/timestamp`;
        const { data } = await axios.get(url);

        const out = new Map();
        for(const ref in data)
        {
            // Convert object keys from string to int
            out.set(parseInt(ref), data[ref]);
        }

        return out;
    }
}
